<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="900px"
    persistent
    scrollable
  >
    <v-card class="border-radius-xl">
      <v-card-title class="d-flex justify-space-between align-center pa-6">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-3 material-icons-round">campaign</v-icon>
          <span class="text-h5 font-weight-bold text-typo">{{ isEdit ? 'Edit Campaign' : 'Create New Campaign' }}</span>
        </div>
        <v-btn icon @click="close" class="ml-4">
          <v-icon class="material-icons-round">close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-6">
        <v-form ref="form" v-model="valid">
          <v-row>
            <!-- Basic Information -->
            <v-col cols="12">
              <v-card class="card-shadow border-radius-lg mb-4">
                <v-card-title class="pb-2">
                  <v-icon color="primary" class="mr-2 material-icons-round">info</v-icon>
                  <span class="text-h6 text-typo">Basic Information</span>
                </v-card-title>
                <v-card-text class="pt-2">
                  <v-row>
                    <v-col cols="12">
                      <v-text-field
                        v-model="form.title"
                        label="Campaign Title"
                        :rules="[rules.required]"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12">
                      <v-textarea
                        v-model="form.description"
                        label="Campaign Description"
                        :rules="[rules.required]"
                        outlined
                        rows="3"
                      ></v-textarea>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.prize_description"
                        label="Prize Description"
                        :rules="[rules.required]"
                        outlined
                        dense
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.prize_value"
                        label="Prize Value ($)"
                        :rules="[rules.required, rules.number]"
                        outlined
                        dense
                        type="number"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.cape_price"
                        label="Cape Price ($)"
                        :rules="[rules.number]"
                        outlined
                        dense
                        type="number"
                        hint="Target price cap for the campaign"
                        persistent-hint
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Campaign Schedule -->
            <v-col cols="12">
              <v-card class="card-shadow border-radius-lg mb-4">
                <v-card-title class="pb-2">
                  <v-icon color="primary" class="mr-2 material-icons-round">schedule</v-icon>
                  <span class="text-h6 text-typo">Campaign Schedule</span>
                </v-card-title>
                <v-card-text class="pt-2">
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-menu
                        v-model="startDateMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            :value="formatDateDisplay(form.start_date)"
                            label="Start Date (MM/DD/YYYY)"
                            readonly
                            outlined
                            dense
                            :rules="[rules.dateRequired]"
                            v-bind="attrs"
                            v-on="on"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="form.start_date"
                          @input="startDateMenu = false"
                          :max="form.end_date"
                        ></v-date-picker>
                      </v-menu>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-menu
                        v-model="endDateMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            :value="formatDateDisplay(form.end_date)"
                            label="End Date (MM/DD/YYYY)"
                            readonly
                            outlined
                            dense
                            :rules="[rules.dateRequired, rules.endDateAfterStart]"
                            v-bind="attrs"
                            v-on="on"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="form.end_date"
                          @input="endDateMenu = false"
                          :min="form.start_date"
                        ></v-date-picker>
                      </v-menu>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Campaign Images -->
            <v-col cols="12">
              <v-card class="card-shadow border-radius-lg mb-4">
                <v-card-title class="pb-2">
                  <v-icon color="primary" class="mr-2 material-icons-round">image</v-icon>
                  <span class="text-h6 text-typo">Campaign Images</span>
                </v-card-title>
                <v-card-text class="pt-2">
                  <v-row>
                    <!-- Hero Image Upload -->
                    <v-col cols="12" md="6">
                      <v-card outlined class="image-upload-card border-radius-lg">
                        <v-card-title class="subtitle-1 pb-2">
                          <v-icon class="mr-2 material-icons-round">wallpaper</v-icon>
                          Hero Image
                        </v-card-title>
                        <v-card-text>
                          <div v-if="form.hero_image_url" class="text-center mb-3 position-relative">
                            <img
                              :src="form.hero_image_url"
                              alt="Hero Image"
                              class="preview-image"
                              @load="onImageLoad('hero')"
                              @error="onImageError('hero', $event)"
                            />
                            <v-btn
                              icon
                              small
                              color="error"
                              class="remove-image-btn"
                              @click="removeHeroImage"
                            >
                              <v-icon small class="material-icons-round">close</v-icon>
                            </v-btn>
                          </div>
                          <v-file-input
                            v-model="heroImageFile"
                            label="Upload Hero Image"
                            accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                            outlined
                            dense
                            @change="handleHeroImageUpload"
                            :loading="uploadingHero"
                            :disabled="uploadingHero"
                            show-size
                          ></v-file-input>
                          <small class="grey--text">Recommended: 1920x1080px, Max: 5MB</small>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <!-- Prize Image Upload -->
                    <v-col cols="12" md="6">
                      <v-card outlined class="image-upload-card border-radius-lg">
                        <v-card-title class="subtitle-1 pb-2">
                          <v-icon class="mr-2 material-icons-round">emoji_events</v-icon>
                          Prize Image
                        </v-card-title>
                        <v-card-text>
                          <div v-if="form.prize_image_url" class="text-center mb-3 position-relative">
                            <img
                              :src="form.prize_image_url"
                              alt="Prize Image"
                              class="preview-image"
                              @load="onImageLoad('prize')"
                              @error="onImageError('prize', $event)"
                            />
                            <v-btn
                              icon
                              small
                              color="error"
                              class="remove-image-btn"
                              @click="removePrizeImage"
                            >
                              <v-icon small class="material-icons-round">close</v-icon>
                            </v-btn>
                          </div>
                          <v-file-input
                            v-model="prizeImageFile"
                            label="Upload Prize Image"
                            accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                            outlined
                            dense
                            @change="handlePrizeImageUpload"
                            :loading="uploadingPrize"
                            :disabled="uploadingPrize"
                            show-size
                          ></v-file-input>
                          <small class="grey--text">Recommended: 800x600px, Max: 5MB</small>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Bonus Promotion & Status -->
            <v-col cols="12">
              <v-card class="card-shadow border-radius-lg mb-4">
                <v-card-title class="pb-2">
                  <v-icon color="primary" class="mr-2 material-icons-round">star</v-icon>
                  <span class="text-h6 text-typo">Bonus Promotion & Status</span>
                </v-card-title>
                <v-card-text class="pt-2">
                  <v-row>
                    <v-col cols="12">
                      <v-switch
                        v-model="form.has_bonus"
                        label="Enable Bonus Promotion"
                        color="primary"
                        class="mt-0"
                      ></v-switch>
                    </v-col>

                    <template v-if="form.has_bonus">
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="form.bonus_multiplier"
                          label="Bonus Multiplier"
                          type="number"
                          outlined
                          dense
                          hint="e.g., 2 for double entries"
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="form.bonus_description"
                          label="Bonus Description"
                          outlined
                          dense
                          placeholder="e.g., Double your entries!"
                        ></v-text-field>
                      </v-col>
                    </template>

                    <v-col cols="12" md="6">
                      <v-select
                        v-model="form.active"
                        :items="statusOptions"
                        label="Campaign Status"
                        outlined
                        dense
                        item-text="text"
                        item-value="value"
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions class="pa-6">
        <v-spacer></v-spacer>
        <v-btn
          color="grey"
          text
          @click="close"
          :disabled="saving"
          class="mr-2"
        >
          Cancel
        </v-btn>
        <v-btn
          color="blue"
          dark
          @click="save"
          :loading="saving"
          class="elevation-2"
        >
          <v-icon left class="material-icons-round">{{ isEdit ? 'edit' : 'add' }}</v-icon>
          {{ isEdit ? 'Update Campaign' : 'Create Campaign' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'SweepstakesCampaignDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    campaign: {
      type: Object,
      default: () => null
    },
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      valid: false,
      saving: false,
      uploadingHero: false,
      uploadingPrize: false,
      startDateMenu: false,
      endDateMenu: false,
      heroImageFile: null,
      prizeImageFile: null,
      form: {
        title: '',
        description: '',
        prize_description: '',
        prize_value: '',
        cape_price: '',
        start_date: '',
        end_date: '',
        hero_image_url: '',
        prize_image_url: '',
        has_bonus: false,
        bonus_multiplier: 2,
        bonus_description: '',
        active: true
      },
      rules: {
        required: value => !!value || 'This field is required',
        number: value => !isNaN(value) && value > 0 || 'Must be a valid positive number',
        dateRequired: value => !!value || 'Date is required',
        endDateAfterStart: value => {
          if (!value || !this.form.start_date) return true
          return new Date(value) > new Date(this.form.start_date) || 'End date must be after start date'
        }
      },
      statusOptions: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false }
      ]
    }
  },
  watch: {
    value(newVal) {
      if (newVal && this.campaign) {
        this.loadCampaignData()
      } else if (newVal) {
        this.resetForm()
      }
    },
    campaign: {
      handler(newCampaign) {
        if (newCampaign && this.value) {
          console.log('Campaign prop changed:', newCampaign)
          this.loadCampaignData()
        }
      },
      immediate: true
    }
  },
  methods: {
    formatDateDisplay(date) {
      if (!date) return ''
      try {
        // If date is in YYYY-MM-DD format, convert to MM/DD/YYYY
        const dateObj = new Date(date)
        if (isNaN(dateObj.getTime())) return date

        return dateObj.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        })
      } catch (error) {
        return date
      }
    },

    formatDateForAPI(date) {
      if (!date) return ''
      try {
        // Ensure date is in YYYY-MM-DD format for API
        const dateObj = new Date(date)
        if (isNaN(dateObj.getTime())) return date

        return dateObj.toISOString().split('T')[0]
      } catch (error) {
        return date
      }
    },

    loadCampaignData() {
      if (this.campaign) {
        this.form = {
          ...this.form,
          ...this.campaign,
          // Ensure dates are in YYYY-MM-DD format for date picker
          start_date: this.formatDateForAPI(this.campaign.start_date),
          end_date: this.formatDateForAPI(this.campaign.end_date),
          // Ensure active status is properly set (boolean)
          active: !!this.campaign.active,
          has_bonus: !!(this.campaign.bonus_promotion),
          bonus_multiplier: this.campaign.bonus_promotion?.multiplier || 2,
          bonus_description: this.campaign.bonus_promotion?.description || '',
          // Ensure image URLs are properly loaded
          hero_image_url: this.campaign.hero_image_url || '',
          prize_image_url: this.campaign.prize_image_url || ''
        }
        console.log('Loaded campaign data:', this.form)
        console.log('Campaign active status:', this.campaign.active, 'Form active:', this.form.active)
      }
    },

    resetForm() {
      // Set default dates: start today, end in 30 days
      const today = new Date()
      const endDate = new Date()
      endDate.setDate(today.getDate() + 30)

      this.form = {
        title: '',
        description: '',
        prize_description: '',
        prize_value: '',
        cape_price: '',
        start_date: today.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        hero_image_url: '',
        prize_image_url: '',
        has_bonus: false,
        bonus_multiplier: 2,
        bonus_description: '',
        active: true
      }
      this.heroImageFile = null
      this.prizeImageFile = null
    },

    async handleHeroImageUpload(file) {
      if (!file) return

      if (file.size > 5 * 1024 * 1024) {
        this.$toast.error('Image size must be less than 5MB')
        return
      }

      try {
        this.uploadingHero = true
        const formData = new FormData()
        formData.append('image', file)
        formData.append('type', 'hero')

        const response = await window.axios.post('/api/sweepstakes-admin/upload-image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: "Bearer " + localStorage.getItem('token')
          },
          timeout: 30000 // 30 second timeout
        })

        if (response.data.flag === 1) {
          this.form.hero_image_url = response.data.data.url
          this.$toast.success('Hero image uploaded successfully')
        } else {
          this.$toast.error(response.data.message || 'Failed to upload hero image')
        }
      } catch (error) {
        console.error('Error uploading hero image:', error)
        if (error.response && error.response.data && error.response.data.message) {
          this.$toast.error(error.response.data.message)
        } else {
          this.$toast.error('Failed to upload hero image')
        }
      } finally {
        this.uploadingHero = false
      }
    },

    async handlePrizeImageUpload(file) {
      if (!file) return

      if (file.size > 5 * 1024 * 1024) {
        this.$toast.error('Image size must be less than 5MB')
        return
      }

      try {
        this.uploadingPrize = true
        const formData = new FormData()
        formData.append('image', file)
        formData.append('type', 'prize')

        const response = await window.axios.post('/api/sweepstakes-admin/upload-image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: "Bearer " + localStorage.getItem('token')
          },
          timeout: 30000 // 30 second timeout
        })

        if (response.data.flag === 1) {
          this.form.prize_image_url = response.data.data.url
          this.$toast.success('Prize image uploaded successfully')
        } else {
          this.$toast.error(response.data.message || 'Failed to upload prize image')
        }
      } catch (error) {
        console.error('Error uploading prize image:', error)
        if (error.response && error.response.data && error.response.data.message) {
          this.$toast.error(error.response.data.message)
        } else {
          this.$toast.error('Failed to upload prize image')
        }
      } finally {
        this.uploadingPrize = false
      }
    },

    async save() {
      if (!this.$refs.form.validate()) {
        return
      }

      try {
        this.saving = true

        const campaignData = {
          title: this.form.title,
          description: this.form.description,
          prize_description: this.form.prize_description,
          prize_value: parseFloat(this.form.prize_value),
          cape_price: this.form.cape_price ? parseFloat(this.form.cape_price) : null,
          start_date: this.formatDateForAPI(this.form.start_date),
          end_date: this.formatDateForAPI(this.form.end_date),
          hero_image_url: this.form.hero_image_url,
          prize_image_url: this.form.prize_image_url,
          active: this.form.active,
          bonus_promotion: this.form.has_bonus ? {
            multiplier: this.form.bonus_multiplier,
            description: this.form.bonus_description
          } : null
        }

        let response
        if (this.isEdit) {
          // Update existing campaign
          response = await window.axios.put(`/api/sweepstakes-admin/campaigns/${this.campaign.id}`, campaignData, {
            headers: {
              Authorization: "Bearer " + localStorage.getItem('token')
            }
          })
        } else {
          // Create new campaign
          response = await window.axios.post('/api/sweepstakes-admin/campaigns', campaignData, {
            headers: {
              Authorization: "Bearer " + localStorage.getItem('token')
            }
          })
        }

        if (response.data.flag === 1) {
          this.$toast.success(response.data.message)
          this.$emit('saved', response.data.campaign)
          this.close()
        } else {
          this.$toast.error(response.data.message || 'Failed to save campaign')
        }

      } catch (error) {
        console.error('Error saving campaign:', error)
        if (error.response && error.response.data && error.response.data.message) {
          this.$toast.error(error.response.data.message)
        } else {
          this.$toast.error(`Failed to ${this.isEdit ? 'update' : 'create'} campaign`)
        }
      } finally {
        this.saving = false
      }
    },

    removeHeroImage() {
      this.form.hero_image_url = ''
      this.heroImageFile = null
      this.$toast.success('Hero image removed')
    },

    removePrizeImage() {
      this.form.prize_image_url = ''
      this.prizeImageFile = null
      this.$toast.success('Prize image removed')
    },

    close() {
      this.$emit('close')
    },

    onImageLoad(type) {
      console.log(`${type} image loaded successfully`)
    },

    onImageError(type, event) {
      console.error(`Failed to load ${type} image:`, event.target.src)
      this.$toast.error(`Failed to load ${type} image. Please check the URL.`)
    }
  }
}
</script>

<style scoped>
/* Use existing admin styles */
.card-shadow {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.border-radius-xl {
  border-radius: 1rem;
}

.border-radius-lg {
  border-radius: 0.75rem;
}

.text-typo {
  color: #344767;
}

.material-icons-round {
  font-family: 'Material Icons Round';
}

.image-upload-card {
  height: 100%;
  transition: all 0.3s ease;
}

.image-upload-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
}

.preview-image {
  max-width: 100%;
  max-height: 200px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

/* Form section spacing */
.v-card-title {
  border-bottom: 1px solid #f0f0f0;
}

/* Switch styling */
.v-input--switch {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

/* Remove image button */
.remove-image-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: rgba(255, 255, 255, 0.9) !important;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.remove-image-btn:hover {
  background-color: rgba(255, 255, 255, 1) !important;
}
</style>
